import React, { useState, useEffect } from 'react';
import api from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Key, Copy, Plus, Trash2, Loader2, CheckCircle, Code, Globe, Settings, Eye, ChevronRight } from 'lucide-react';
import { AgentChat } from '@/components/agents/AgentChat';

interface EmbedSession {
  id: string;
  started_at: string;
  last_active_at: string;
  origin: string;
  messages_count: number;
}

interface EmbedToken {
  id: string;
  name: string;
  token_hint: string;
  allowed_agents: string[];
  allowed_origins: string[];
  rate_limit_per_minute: number;
  active: boolean;
  usage_count: number;
  expires_at: string | null;
  created_at: string;
  sessions?: EmbedSession[];
}

interface CreateTokenForm {
  name: string;
  allowed_agents: string;
  allowed_origins: string;
  rate_limit_per_minute: number;
  expires_at: string;
}

const initialForm: CreateTokenForm = {
  name: '',
  allowed_agents: '',
  allowed_origins: '',
  rate_limit_per_minute: 60,
  expires_at: '',
};

export function EmbedManagementPage() {
  const { toast } = useToast();
  const [tokens, setTokens] = useState<EmbedToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tokenToDelete, setTokenToDelete] = useState<EmbedToken | null>(null);
  const [form, setForm] = useState<CreateTokenForm>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [newPlaintextToken, setNewPlaintextToken] = useState<string | null>(null);
  const [expandedTokenId, setExpandedTokenId] = useState<string | null>(null);
  const [tokenCopied, setTokenCopied] = useState(false);

  // Preview tab state
  const [previewAgent, setPreviewAgent] = useState('');
  const [previewTokenId, setPreviewTokenId] = useState('');
  const [previewColor, setPreviewColor] = useState('#2563eb');
  const [previewTitle, setPreviewTitle] = useState('Chat');
  const [snippetCopied, setSnippetCopied] = useState(false);

  useEffect(() => {
    fetchTokens();
  }, []);

  async function fetchTokens() {
    setLoading(true);
    try {
      const res = await api.get('/embed-admin/tokens');
      setTokens(res.data ?? []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load embed tokens.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateToken() {
    if (!form.name.trim()) {
      toast({ title: 'Validation', description: 'Token name is required.', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name.trim(),
        allowed_agents: form.allowed_agents
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        allowed_origins: form.allowed_origins
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        rate_limit_per_minute: form.rate_limit_per_minute,
      };

      if (form.expires_at) {
        payload.expires_at = form.expires_at;
      }

      const res = await api.post('/embed-admin/tokens', payload);
      const { plaintext_token } = res.data;

      setNewPlaintextToken(plaintext_token ?? null);
      setCreateDialogOpen(false);
      setForm(initialForm);
      setTokenCopied(false);
      await fetchTokens();

      toast({ title: 'Token created', description: 'Copy the token now. It will not be shown again.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to create embed token.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggleActive(token: EmbedToken) {
    try {
      await api.put('/embed-admin/tokens/' + token.id, { active: !token.active });
      setTokens((prev) =>
        prev.map((t) => (t.id === token.id ? { ...t, active: !t.active } : t))
      );
      toast({ title: token.active ? 'Token deactivated' : 'Token activated' });
    } catch {
      toast({ title: 'Error', description: 'Failed to update token.', variant: 'destructive' });
    }
  }

  async function handleDeleteToken() {
    if (!tokenToDelete) return;
    setSubmitting(true);
    try {
      await api.delete('/embed-admin/tokens/' + tokenToDelete.id);
      toast({ title: 'Token deleted', description: `"${tokenToDelete.name}" has been permanently deleted.` });
      setDeleteDialogOpen(false);
      if (expandedTokenId === tokenToDelete.id) {
        setExpandedTokenId(null);
      }
      setTokenToDelete(null);
      await fetchTokens();
    } catch {
      toast({ title: 'Error', description: 'Failed to delete token.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  }

  function openDeleteDialog(token: EmbedToken) {
    setTokenToDelete(token);
    setDeleteDialogOpen(true);
  }

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied', description: `${label} copied to clipboard.` });
  }

  function toggleExpanded(tokenId: string) {
    setExpandedTokenId((prev) => (prev === tokenId ? null : tokenId));
  }

  function formatDateTime(dateStr: string | null) {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  }

  function getEmbedSnippet() {
    return `<script src="${window.location.origin}/embed.js" data-token="YOUR_TOKEN"></script>`;
  }

  function getWidgetConfigPreview(token: EmbedToken) {
    return JSON.stringify(
      {
        token: `****${token.token_hint}`,
        allowedAgents: token.allowed_agents,
        allowedOrigins: token.allowed_origins,
        rateLimitPerMinute: token.rate_limit_per_minute,
      },
      null,
      2
    );
  }

  // Build the preview embed snippet based on current configuration
  function getPreviewSnippet(): string {
    const selectedToken = tokens.find((t) => t.id === previewTokenId);
    const tokenHint = selectedToken ? `****${selectedToken.token_hint}` : 'YOUR_TOKEN';
    const agentName = previewAgent.trim() || 'AGENT_NAME';
    return `<script src="${window.location.origin}/embed.js" data-token="${tokenHint}" data-agent="${agentName}"></script>`;
  }

  const activeTokens = tokens.filter((t) => t.active);

  return (
    <div className="container mx-auto max-w-6xl py-8 px-4">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4">
        <span>Developer</span>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-foreground font-medium">Embed Tokens</span>
      </nav>

      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Code className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold tracking-tight">Embed Tokens</h1>
          </div>
          <p className="text-muted-foreground">
            Manage tokens for embedding agents in external applications.
          </p>
        </div>
        <Button onClick={() => { setForm(initialForm); setCreateDialogOpen(true); }} className="gap-2">
          <Plus className="h-4 w-4" />
          Create Token
        </Button>
      </div>

      {/* Show plaintext token alert after creation */}
      {newPlaintextToken && (
        <Alert className="mb-6 border-amber-500/50 bg-amber-500/10">
          <Key className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-600">New Token Created</AlertTitle>
          <AlertDescription>
            <p className="text-sm text-amber-600 mb-3 font-medium">
              This token will only be shown once. Copy it and store it securely.
            </p>
            <div className="flex items-center gap-2 mb-2">
              <Input
                readOnly
                value={newPlaintextToken}
                className="font-mono text-sm bg-amber-50 dark:bg-amber-950 border-amber-300"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  copyToClipboard(newPlaintextToken, 'Embed token');
                  setTokenCopied(true);
                }}
              >
                {tokenCopied ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-amber-600 hover:text-amber-700"
              onClick={() => setNewPlaintextToken(null)}
            >
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs: Tokens and Preview */}
      <Tabs defaultValue="tokens" className="space-y-6">
        <TabsList>
          <TabsTrigger value="tokens" className="gap-2">
            <Key className="h-4 w-4" />
            Tokens
          </TabsTrigger>
          <TabsTrigger value="preview" className="gap-2">
            <Eye className="h-4 w-4" />
            Preview
          </TabsTrigger>
        </TabsList>

        {/* Tokens Tab */}
        <TabsContent value="tokens">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : tokens.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <Code className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No embed tokens</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-md">
                  Create a token to embed agents in external websites and applications.
                </p>
                <Button onClick={() => { setForm(initialForm); setCreateDialogOpen(true); }} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Create Token
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Token</TableHead>
                    <TableHead>Allowed Agents</TableHead>
                    <TableHead>Allowed Origins</TableHead>
                    <TableHead className="text-center">Rate Limit</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-center">Usage</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tokens.map((token) => (
                    <React.Fragment key={token.id}>
                      <TableRow
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleExpanded(token.id)}
                      >
                        <TableCell className="font-medium">{token.name}</TableCell>
                        <TableCell>
                          <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">
                            ****{token.token_hint}
                          </code>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {token.allowed_agents.length === 0 ? (
                              <span className="text-xs text-muted-foreground">All</span>
                            ) : (
                              token.allowed_agents.slice(0, 2).map((agent) => (
                                <Badge key={agent} variant="outline" className="text-xs">
                                  {agent}
                                </Badge>
                              ))
                            )}
                            {token.allowed_agents.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{token.allowed_agents.length - 2}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {token.allowed_origins.length === 0 ? (
                              <span className="text-xs text-muted-foreground">Any</span>
                            ) : (
                              token.allowed_origins.slice(0, 2).map((origin) => (
                                <Badge key={origin} variant="secondary" className="text-xs max-w-[140px] truncate">
                                  {origin}
                                </Badge>
                              ))
                            )}
                            {token.allowed_origins.length > 2 && (
                              <Badge variant="secondary" className="text-xs">
                                +{token.allowed_origins.length - 2}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-sm">{token.rate_limit_per_minute}/min</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={token.active ? 'default' : 'secondary'}>
                            {token.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-sm font-mono">{token.usage_count.toLocaleString()}</span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDateTime(token.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            <Switch
                              checked={token.active}
                              onCheckedChange={() => handleToggleActive(token)}
                              aria-label={`Toggle ${token.name}`}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => openDeleteDialog(token)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>

                      {/* Expanded Row */}
                      {expandedTokenId === token.id && (
                        <TableRow>
                          <TableCell colSpan={9} className="bg-muted/30 p-0">
                            <div className="p-6 space-y-6">
                              {/* Sessions */}
                              <div>
                                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                                  <Globe className="h-4 w-4" />
                                  Recent Sessions
                                </h4>
                                {(!token.sessions || token.sessions.length === 0) ? (
                                  <p className="text-sm text-muted-foreground">No sessions recorded yet.</p>
                                ) : (
                                  <div className="rounded-md border">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>Session ID</TableHead>
                                          <TableHead>Origin</TableHead>
                                          <TableHead>Started</TableHead>
                                          <TableHead>Last Active</TableHead>
                                          <TableHead className="text-center">Messages</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {token.sessions.map((session) => (
                                          <TableRow key={session.id}>
                                            <TableCell className="font-mono text-xs">
                                              {session.id.slice(0, 8)}...
                                            </TableCell>
                                            <TableCell className="text-sm">{session.origin}</TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                              {formatDateTime(session.started_at)}
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                              {formatDateTime(session.last_active_at)}
                                            </TableCell>
                                            <TableCell className="text-center">{session.messages_count}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                )}
                              </div>

                              <Separator />

                              {/* Widget Config Preview */}
                              <div>
                                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                                  <Settings className="h-4 w-4" />
                                  Widget Configuration
                                </h4>
                                <pre className="bg-muted rounded-lg p-4 text-xs font-mono overflow-x-auto">
                                  {getWidgetConfigPreview(token)}
                                </pre>
                              </div>

                              <Separator />

                              {/* Embed Code Snippet */}
                              <div>
                                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                                  <Code className="h-4 w-4" />
                                  Embed Code
                                </h4>
                                <div className="relative">
                                  <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-4 text-sm font-mono overflow-x-auto">
                                    <code>{getEmbedSnippet()}</code>
                                  </pre>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    className="absolute top-2 right-2 gap-1.5"
                                    onClick={() => copyToClipboard(getEmbedSnippet(), 'Embed snippet')}
                                  >
                                    <Copy className="h-3 w-3" />
                                    Copy
                                  </Button>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2">
                                  Replace <code className="bg-muted px-1 py-0.5 rounded">YOUR_TOKEN</code> with your actual token value.
                                </p>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* Preview Tab */}
        <TabsContent value="preview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left panel: Configuration */}
            <Card>
              <CardContent className="p-6 space-y-5">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Widget Configuration
                </h3>

                <div className="space-y-2">
                  <Label htmlFor="preview-agent">Agent Name</Label>
                  <Input
                    id="preview-agent"
                    placeholder="e.g. my-support-agent"
                    value={previewAgent}
                    onChange={(e) => setPreviewAgent(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter the agent name to use in the embedded widget.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="preview-token">Token</Label>
                  <select
                    id="preview-token"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={previewTokenId}
                    onChange={(e) => setPreviewTokenId(e.target.value)}
                  >
                    <option value="">Select a token...</option>
                    {activeTokens.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} (****{t.token_hint})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground">
                    Choose an active token. Only active tokens are listed.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="preview-color">Theme Color</Label>
                  <div className="flex items-center gap-3">
                    <input
                      id="preview-color"
                      type="color"
                      value={previewColor}
                      onChange={(e) => setPreviewColor(e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-input"
                    />
                    <Input
                      value={previewColor}
                      onChange={(e) => setPreviewColor(e.target.value)}
                      className="font-mono text-sm max-w-[140px]"
                      placeholder="#2563eb"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="preview-title">Custom Title</Label>
                  <Input
                    id="preview-title"
                    placeholder="Chat"
                    value={previewTitle}
                    onChange={(e) => setPreviewTitle(e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Right panel: Live Preview */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                  <Eye className="h-5 w-5" />
                  Live Preview
                </h3>

                {previewAgent.trim() ? (
                  <div
                    className="border rounded-lg overflow-hidden"
                    style={{ height: 460 }}
                  >
                    <AgentChat
                      agentId={previewAgent.trim()}
                      agentName={previewAgent.trim()}
                      mode="compact"
                      authMode="token"
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[460px] border rounded-lg bg-muted/30 text-center">
                    <Eye className="h-10 w-10 text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">
                      Enter an agent name to see the widget preview.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Embed Code Snippet */}
          <Card className="mt-6">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Code className="h-4 w-4" />
                Embed Code
              </h3>
              <div className="relative">
                <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-4 text-sm font-mono overflow-x-auto">
                  <code>{getPreviewSnippet()}</code>
                </pre>
                <Button
                  variant="secondary"
                  size="sm"
                  className="absolute top-2 right-2 gap-1.5"
                  onClick={() => {
                    copyToClipboard(getPreviewSnippet(), 'Embed snippet');
                    setSnippetCopied(true);
                    setTimeout(() => setSnippetCopied(false), 2000);
                  }}
                >
                  {snippetCopied ? (
                    <>
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Add this snippet to your website. Replace the token hint with your full token value.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Token Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Embed Token</DialogTitle>
            <DialogDescription>
              Generate a new token for embedding agents in external applications.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="token-name">Token Name</Label>
              <Input
                id="token-name"
                placeholder="e.g. Marketing Site Widget"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="allowed-agents">Allowed Agents</Label>
              <Input
                id="allowed-agents"
                placeholder="agent-1, agent-2 (leave empty for all)"
                value={form.allowed_agents}
                onChange={(e) => setForm({ ...form, allowed_agents: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Comma-separated list of agent IDs. Leave empty to allow all agents.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="allowed-origins">Allowed Origins</Label>
              <Input
                id="allowed-origins"
                placeholder="https://example.com, https://app.example.com"
                value={form.allowed_origins}
                onChange={(e) => setForm({ ...form, allowed_origins: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Comma-separated list of allowed origin URLs. Leave empty to allow any origin.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rate-limit">Rate Limit (requests per minute)</Label>
              <Input
                id="rate-limit"
                type="number"
                min={1}
                max={10000}
                value={form.rate_limit_per_minute}
                onChange={(e) => setForm({ ...form, rate_limit_per_minute: parseInt(e.target.value, 10) || 60 })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expiry-date">Expiry Date (optional)</Label>
              <Input
                id="expiry-date"
                type="date"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty for a non-expiring token.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleCreateToken} disabled={submitting} className="gap-2">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Token
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Embed Token</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{tokenToDelete?.name}</strong>?
              Any applications using this token will immediately lose access.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteToken} disabled={submitting} className="gap-2">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Delete Token
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default EmbedManagementPage;
